import type { ReactNode } from 'react';

import { bold, InlineText, TimelineEvent } from '../components/shared';
import { TIMELINE_DESIGN_TOKENS } from '../design-tokens';
import type { OutboundEventPayload } from '../types';
import { EVENT_TYPES } from '../types';
import { outboundOutcomeReason } from './outboundOutcomeCopy';
import type { EventComponentProps } from './types';

const META_TEXT_CLASS = `${TIMELINE_DESIGN_TOKENS.typography.subItem} text-secondary`;

export function OutboundChannelEvent({ event }: EventComponentProps) {
  const payload = event.payload as OutboundEventPayload | undefined;
  const eventTypeName: string = event.event_type_name;

  let headerText: string;
  // Set only when the header needs a non-bold reason clause alongside the
  // bold event verb (Failed / attempt-failed cases); other cases fall back
  // to a fully-bold header built from headerText below.
  let header: ReactNode | undefined;
  const metaLines: string[] = [];

  switch (eventTypeName) {
    case EVENT_TYPES.AHOY_OUTBOUND_REQUESTED:
      headerText = 'Outbound request queued';
      break;
    case EVENT_TYPES.AHOY_OUTBOUND_DISPATCHED: {
      headerText = 'Dispatched';
      const raw = payload?.external?.status_code;
      let n = NaN;
      if (typeof raw === 'string' && raw.trim() !== '') {
        n = Number(raw);
      } else if (typeof raw === 'number') {
        n = raw;
      }
      if (Number.isFinite(n)) {
        metaLines.push(`HTTP ${n}`);
      }
      const providerMessageId = payload?.external?.provider_message_id;
      if (providerMessageId) {
        metaLines.push(`Message ${providerMessageId}`);
      }
      break;
    }
    case EVENT_TYPES.AHOY_OUTBOUND_FAILED: {
      const reason = outboundOutcomeReason(payload?.reason);
      headerText = reason ? `Failed: ${reason}` : 'Failed';
      header = (
        <InlineText>
          {bold('Failed')}
          {reason ? <>: {reason}</> : null}
        </InlineText>
      );
      const attempt = payload?.attempt_count;
      if (Number.isFinite(attempt)) {
        metaLines.push(`${attempt} attempt(s)`);
      }
      break;
    }
    case EVENT_TYPES.AHOY_OUTBOUND_ATTEMPT_FAILED: {
      const attempt = payload?.attempt_count;
      const base = Number.isFinite(attempt)
        ? `Attempt ${attempt} failed`
        : 'Attempt failed';
      const reason = outboundOutcomeReason(payload?.reason);
      headerText = reason ? `${base}: ${reason}` : base;
      header = (
        <InlineText>
          {bold(base)}
          {reason ? <>: {reason}</> : null}
        </InlineText>
      );
      break;
    }
    case EVENT_TYPES.AHOY_OUTBOUND_ATTEMPT_CLAIMED: {
      const attempt = payload?.attempt_count;
      headerText = Number.isFinite(attempt)
        ? `Attempt ${attempt} claimed`
        : 'Attempt claimed';
      break;
    }
    case EVENT_TYPES.AHOY_OUTBOUND_REPLAY_REQUESTED:
      headerText = 'Replay requested';
      break;
    default:
      headerText = 'Outbound event';
  }

  const metaNode = metaLines.length ? (
    <div className={META_TEXT_CLASS}>{metaLines.join(' · ')}</div>
  ) : null;

  return (
    <TimelineEvent ariaLabel={headerText}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={header ?? <InlineText>{bold(headerText)}</InlineText>}
        timestamp={event.occurred_at}
      >
        {metaNode}
      </TimelineEvent.Content>
    </TimelineEvent>
  );
}
